/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */

/**
 * The legend component for Chart
 *
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Maxim Fedorov
 * @author Aleksandr Perkhunov
 * @version 1.3.1
 * @since 1.1.0
 */

import { FC } from 'react';

import classNames from 'classnames';

import { LayoutStyled } from '../../Block/Layout';
import { Checkbox, EllipseText } from '../../index';
import { EChartImplementationType } from '../ChartsImplementation/chartImplementationTypes';
import {
    EChartDataKeyEnum,
    IChartDataInput,
    IChartLabels,
    TMapNameToColor,
    TMapNameToTranslation,
} from '../chartTypes';
import {
    ChartLegendItemSubTitleStyled,
    ChartLegendItemTextStyled,
    ChartLegendWrapperStyled,
} from './styled/ChartLegendStyled';

export interface IChartLegendProps {
    chartType: EChartImplementationType;
    className?: string;
    dataName?: string;
    items: IChartDataInput[];
    legendRow: number;
    mapIdToLabel: IChartLabels;
    mapNameToColor: TMapNameToColor;
    mapNameToTranslation: TMapNameToTranslation;
    onClick?: (item: IChartDataInput) => void;
    withSubtitle?: boolean;
}

export const ChartLegend: FC<IChartLegendProps> = props => {

    const isOneItemChecked = props.items.filter((item: IChartDataInput) => item.checked && item.total).length < 2;

    const setLegendItemClassName = (item: IChartDataInput): string => {
        return classNames('legend__item mb-3 pl-3', {
            'checked': item.checked,
            'disabled': item.total === 0,
        });
    };

    const setLegendContainerClassname: string = classNames(props.className, {
        'one-item-checked': isOneItemChecked,
    });

    const onLegendItemClick = (item: IChartDataInput): void => {
        if (item.total === 0 || (item.checked && isOneItemChecked)) {
            return;
        }
        props.onClick?.(item);
    };

    const calcLegendHeight: number = props.legendRow > props.items.length
        ? props.items.length * 32 + 24
        : props.legendRow * 32 + 24;

    return (
        <ChartLegendWrapperStyled
            className={setLegendContainerClassname}
            data-name={props.dataName}
            maxHeight={calcLegendHeight}
        >
            {
                props.items.map((item: IChartDataInput) =>
                    item[EChartDataKeyEnum.x] &&
                    <LayoutStyled
                        className={setLegendItemClassName(item)}
                        key={item[EChartDataKeyEnum.x]}
                        style={{
                            order: item.order,
                        }}
                    >
                        <Checkbox
                            checked={item.checked}
                            className="legend__item-marker"
                            colorBorderDisabled="transparent"
                            colorChecked={props.mapNameToColor[item[EChartDataKeyEnum.x]]}
                            colorDisabled={`${props.mapNameToColor[item[EChartDataKeyEnum.x]]}80`}
                            disabled={item.total === 0}
                            isNotChangeable={item.checked && isOneItemChecked}
                            kind="normal"
                            onChange={() => onLegendItemClick(item)}
                        />
                        <EllipseText
                            title={props.mapNameToTranslation[item[EChartDataKeyEnum.x]]}
                            wrapper={ChartLegendItemTextStyled}
                            wrapperProps={{
                                onClick: () => onLegendItemClick(item),
                            }}
                        >
                                {
                                    props.chartType === EChartImplementationType.category ?
                                    props.mapNameToTranslation[item[EChartDataKeyEnum.x]] :
                                    props.mapIdToLabel[item[EChartDataKeyEnum.x]]?.title ?? item[EChartDataKeyEnum.x]
                                }
                        </EllipseText>
                        {
                            props.withSubtitle &&
                            <EllipseText
                                title={props.mapIdToLabel[item[EChartDataKeyEnum.x]]?.subTitle ?? ''}
                                wrapper={ChartLegendItemSubTitleStyled}
                                wrapperProps={{
                                    onClick: () => onLegendItemClick(item),
                                }}
                            >
                                {props.mapIdToLabel[item[EChartDataKeyEnum.x]]?.subTitle ?? ''}
                            </EllipseText>
                        }
                    </LayoutStyled>,
                )
            }
        </ChartLegendWrapperStyled>
    );
};

export default ChartLegend;
